import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar, Clock, User, AlertTriangle, Edit, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { DateTimeSelector } from "./DateTimeSelector";
import { sendRescheduleNotification, sendCancellationNotification } from "@/utils/emailService";

interface BookingDetails {
  id: string;
  provider: {
    name: string;
    specialty: string;
  };
  date: string;
  time: string;
  patient: {
    name: string;
    email: string;
    phone: string;
  };
  reason: string;
  status: "confirmed" | "cancelled" | "rescheduled" | "completed";
}

interface BookingManagementProps {
  booking: BookingDetails;
  onBack: () => void;
  onBookingUpdated: (updatedBooking: BookingDetails) => void;
}

export function BookingManagement({ booking, onBack, onBookingUpdated }: BookingManagementProps) {
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime) {
      toast({
        title: "Validation Error",
        description: "Please select both date and time",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    const newDate = selectedDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    try {
      // Send reschedule notification
      await sendRescheduleNotification(
        booking.id,
        booking.patient.email,
        booking.patient.name,
        booking.provider.name,
        newDate,
        selectedTime,
        booking.date,
        booking.time
      );

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const updatedBooking = {
        ...booking,
        date: newDate,
        time: selectedTime,
        status: "rescheduled" as const
      };
      
      onBookingUpdated(updatedBooking);
      setIsRescheduleOpen(false);
      
      toast({
        title: "Appointment Rescheduled",
        description: "Your appointment has been successfully rescheduled. A confirmation email has been sent.",
      });
    } catch (error) {
      toast({
        title: "Reschedule Error",
        description: "There was an error rescheduling your appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = async () => {
    setIsLoading(true);
    
    try {
      // Send cancellation notification
      await sendCancellationNotification(
        booking.id,
        booking.patient.email,
        booking.patient.name,
        booking.provider.name,
        booking.date,
        booking.time
      );

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const updatedBooking = {
        ...booking,
        status: "cancelled" as const
      };
      
      onBookingUpdated(updatedBooking);
      setIsCancelOpen(false);
      
      toast({
        title: "Appointment Cancelled",
        description: "Your appointment has been cancelled. A confirmation email has been sent.",
      });
    } catch (error) {
      toast({
        title: "Cancellation Error",
        description: "There was an error cancelling your appointment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "rescheduled":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const canModify = booking.status === "confirmed";

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Booking</h1>
          <p className="text-gray-600">Booking ID: {booking.id}</p>
        </div>
        <Badge className={getStatusColor(booking.status)}>
          {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
        </Badge>
      </div>

      {/* Appointment Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Appointment Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="font-medium">{booking.provider.name}</div>
                  <div className="text-sm text-blue-600">{booking.provider.specialty}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="font-medium">{booking.date}</div>
                  <div className="text-sm text-gray-600 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {booking.time}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="font-medium text-sm text-gray-700">Patient</div>
                <div className="text-gray-900">{booking.patient.name}</div>
              </div>
              <div>
                <div className="font-medium text-sm text-gray-700">Reason for Visit</div>
                <div className="text-sm text-gray-600">{booking.reason}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      {canModify && (
        <Card>
          <CardHeader>
            <CardTitle>Manage Your Appointment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button 
                onClick={() => setIsRescheduleOpen(true)} 
                variant="outline"
                className="flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Reschedule
              </Button>
              <Button 
                onClick={() => setIsCancelOpen(true)} 
                variant="outline"
                className="flex items-center gap-2 text-red-600 hover:text-red-700"
              >
                <X className="w-4 h-4" />
                Cancel
              </Button>
            </div>
            <p className="text-sm text-gray-600">
              You can reschedule or cancel your appointment up to 24 hours before the scheduled time.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Back Button */}
      <Button onClick={onBack} variant="outline" className="w-full">
        Back to Bookings
      </Button>

      {/* Reschedule Dialog */}
      <Dialog open={isRescheduleOpen} onOpenChange={setIsRescheduleOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Reschedule Appointment</DialogTitle>
            <DialogDescription>
              Select a new date and time for your appointment with {booking.provider.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <DateTimeSelector
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              onDateSelect={setSelectedDate}
              onTimeSelect={setSelectedTime}
            />
            <div className="flex gap-3 pt-4">
              <Button 
                onClick={() => setIsRescheduleOpen(false)} 
                variant="outline" 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleReschedule} 
                className="flex-1"
                disabled={isLoading || !selectedDate || !selectedTime}
              >
                {isLoading ? "Rescheduling..." : "Confirm Reschedule"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Cancel Appointment
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your appointment with {booking.provider.name} on {booking.date} at {booking.time}?
            </DialogDescription>
          </DialogHeader>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800">
              <strong>Please note:</strong> Cancelling less than 24 hours before your appointment may result in a cancellation fee.
            </p>
          </div>
          <div className="flex gap-3 pt-4">
            <Button 
              onClick={() => setIsCancelOpen(false)} 
              variant="outline" 
              className="flex-1"
            >
              Keep Appointment
            </Button>
            <Button 
              onClick={handleCancel} 
              variant="destructive" 
              className="flex-1"
              disabled={isLoading}
            >
              {isLoading ? "Cancelling..." : "Cancel Appointment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
